import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { Field, ControlSelect, Checkbox } from '../../common/components/Form';
import { PrimaryButton } from '../../common';
import { Spacing } from '../../assets/GlobalStyle/Variables';
import { DeceasedTableParams } from '../../api/protocolsApi';
import { Options } from './useProtocolList';

type ProtocolFilterFormData = {
  deceased_name: string;
  protocol_number: string;
  protocol_year: number | null;
  birth_id: string;
  doctor_id: number | null;
  doctor2_id: number | null;
  allPatients: boolean;
  onlyOpen: boolean;
  onlyClosed: boolean;
};

type ProtocolFilterProps = {
  onSubmit: (data: DeceasedTableParams) => void;
  options: Options;
};

export const ProtocolFilter: FC<ProtocolFilterProps> = ({ onSubmit, options }) => {
  const methods = useForm<ProtocolFilterFormData>({
    defaultValues: {
      deceased_name: '',
      protocol_number: '',
      protocol_year: null,
      birth_id: '',
      doctor_id: null,
      doctor2_id: null,
      allPatients: true,
      onlyOpen: false,
      onlyClosed: false
    }
  });

  const { watch, setValue } = methods;
  const allPatients = watch('allPatients');
  const onlyOpen = watch('onlyOpen');
  const onlyClosed = watch('onlyClosed');

  useEffect(() => {
    if (allPatients) {
      setValue('onlyOpen', false);
      setValue('onlyClosed', false);
    }
  }, [allPatients, setValue]);

  useEffect(() => {
    if (onlyOpen) {
      setValue('allPatients', false);
      setValue('onlyClosed', false);
    }
  }, [onlyOpen, setValue]);

  useEffect(() => {
    if (onlyClosed) {
      setValue('allPatients', false);
      setValue('onlyOpen', false);
    }
  }, [onlyClosed, setValue]);

  const handleSubmit = (data: ProtocolFilterFormData) => {
    const params: DeceasedTableParams = {};

    if (data.deceased_name) {
      params.deceased_name = data.deceased_name;
    }

    if (data.protocol_number) {
      params.protocol_number = parseInt(data.protocol_number);
    }

    if (data.protocol_year) {
      params.protocol_year = data.protocol_year;
    }

    if (data.birth_id) {
      params.birth_id = data.birth_id;
    }

    if (data.doctor_id) {
      params.doctor_id = data.doctor_id;
    }

    if (data.doctor2_id) {
      params.doctor2_id = data.doctor2_id;
    }

    if (data.onlyOpen) {
      params.is_locked = false;
    }

    if (data.onlyClosed) {
      params.is_locked = true;
    }

    onSubmit(params);
  };

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={methods.handleSubmit(handleSubmit)}>
        <Row>
          <Col md={4}>
            <Field id="deceased_name" name="deceased_name" label="Jméno zemřelého" placeholder="" />
          </Col>
          <Col md={4}>
            <Field
              id="protocol_number"
              name="protocol_number"
              label="Číslo protokolu"
              placeholder=""
            />
          </Col>
          <Col md={4}>
            <ControlSelect
              id="protocol_year"
              name="protocol_year"
              label="Rok"
              options={options.years}
              placeholder="Vyberte rok"
              autoTransform
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Field id="birth_id" name="birth_id" label="Rodné číslo" placeholder="" />
          </Col>
          <Col md={4}>
            <ControlSelect
              id="doctor_id"
              name="doctor_id"
              label="Pitvající 1"
              options={options.employees}
              placeholder="Vyberte lékaře"
              autoTransform
            />
          </Col>
          <Col md={4}>
            <ControlSelect
              id="doctor2_id"
              name="doctor2_id"
              label="Pitvající 2"
              options={options.employees}
              placeholder="Vyberte lékaře"
              autoTransform
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <StyledCheckboxGroup>
              <Checkbox id="allPatients" name="allPatients" label="všichni zemřelí" />
              <Checkbox id="onlyOpen" name="onlyOpen" label="pouze neuzavření" />
              <Checkbox id="onlyClosed" name="onlyClosed" label="pouze uzavření" />
            </StyledCheckboxGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <StyledButtonWrapper>
              <PrimaryButton type="submit">Hledat</PrimaryButton>
            </StyledButtonWrapper>
          </Col>
        </Row>
      </StyledForm>
    </FormProvider>
  );
};

const StyledForm = styled.form`
  margin-bottom: ${Spacing(8)};
  margin-top: ${Spacing(8)};
`;

const StyledCheckboxGroup = styled.div`
  display: flex;
  gap: ${Spacing(6)};
  align-items: center;
  margin-top: ${Spacing(8)};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${Spacing(4)};
`;
